import { useRef } from 'react'

import { useFrame } from '../../../engine'
import { seg } from '../../../motion'
import { useCopy } from '../../i18n'
import { C, Shape } from '../shapes'

// Kacheln im Raster (4 Spalten), Reihenfolge = Fallreihenfolge
const TILES = [
  { span: 2, bg: C.blue, fg: '#fff', shape: ['half', '#FFFFFF'] },
  { bg: C.green, shape: ['tri', C.ink] },
  { bg: C.yellow, shape: ['circle', C.ink] },
  { bg: C.pink, shape: ['square', C.ink] },
  { span: 2, bg: '#FFFFFF', fg: C.blue, shape: ['quarter', C.yellow] },
  { bg: C.ink, fg: '#fff', shape: ['tri', C.green] },
  { bg: C.markBlue, fg: '#fff', shape: ['circle', C.pink] },
  { bg: '#FFFFFF', fg: C.ink, shape: ['square', C.pink] }
]
const SEALS = [
  ['square', C.green],
  ['tri', C.yellow],
  ['circle', C.pink]
]
const FALL = 0.075
const STEP = 0.068
const START = 0.1
const at = (i) => START + i * STEP

/**
 * Die Zahlen aus den Fallstudien fallen wie Tetris-Steine ins Raster,
 * kleine Kacheln drehen sich dabei in 90-Grad-Schritten. Die letzte Kachel trägt die Siegel.
 */
export default function PlProof() {
  const c = useCopy('belege')
  const refs = useRef([])

  useFrame((l) => {
    refs.current.forEach((el, i) => {
      if (!el) return
      const a = at(i)
      const p = seg(l, a, a + FALL)
      const land = seg(l, a + FALL, a + FALL + 0.05)
      if (p <= 0) {
        el.style.opacity = '0'
        return
      }
      el.style.opacity = '1'
      // Fallen mit Schwerkraft, Drehen in Stufen, beim Aufsetzen kurz stauchen
      const y = (1 - p * p) * -110
      const wide = !TILES[i] || TILES[i].span
      const rot = wide ? 0 : (3 - Math.min(3, Math.floor(p * 4))) * 90
      const squash =
        land > 0 && land < 1 ? Math.sin(land * Math.PI) * (1 - land) : 0
      el.style.transform =
        `translateY(${y.toFixed(2)}vh) rotate(${rot}deg) ` +
        `scale(${(1 + squash * 0.08).toFixed(3)}, ${(1 - squash * 0.14).toFixed(3)})`
    })
  })

  return (
    <div className='ap-proof'>
      <div className='ap-proof__head'>
        <div className='ap-eyebrow'>{c.eyebrow}</div>
        <h2 className='ap-h1'>{c.title}</h2>
      </div>

      <div className='ap-proof__grid'>
        {c.tiles.map((t, i) => {
          const tile = TILES[i]
          return (
            <div
              key={t.label}
              ref={(el) => (refs.current[i] = el)}
              className={`ap-proof__tile ${tile.bg === '#FFFFFF' ? 'is-white' : ''}`}
              style={{
                gridColumn: tile.span ? `span ${tile.span}` : undefined,
                background: tile.bg,
                color: tile.fg || C.ink,
                opacity: 0
              }}
            >
              <Shape
                kind={tile.shape[0]}
                color={tile.shape[1]}
                size={34}
                className='ap-proof__shape'
              />
              <span className='ap-proof__num'>{t.value}</span>
              <span className='ap-proof__label'>{t.label}</span>
              <span className='ap-proof__source'>{t.source}</span>
            </div>
          )
        })}
        <div
          ref={(el) => (refs.current[TILES.length] = el)}
          className='ap-proof__tile ap-proof__tile--seals is-white'
          style={{ gridColumn: 'span 2', opacity: 0 }}
        >
          <ul className='ap-proof__seals'>
            {c.badges.map((b, i) => (
              <li key={b}>
                <Shape kind={SEALS[i][0]} color={SEALS[i][1]} size={18} />
                {b}
              </li>
            ))}
          </ul>
          <span className='ap-proof__science'>{c.science}</span>
        </div>
      </div>
    </div>
  )
}
